import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import PortalDialogType from "../../PortalDialogType";
import {WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import React, {createRef, CSSProperties, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import {TabPanel, TabView} from "primereact/tabview";
import {Dropdown} from "primereact/dropdown";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {InputNumber} from "primereact/inputnumber";
import {Slider} from "primereact/slider";
import {InputSwitch} from "primereact/inputswitch";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import ConvertDialogFields from "../../field/convert/ConvertDialogFields";
import {Button} from "primereact/button";
import PaperHelper, {PaperFormat, paperSizes} from "../../../../shared/helper/PaperHelper";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {InputText} from "primereact/inputtext";
import ImageUnit from "../../../../shared/units/ImageUnit";
import {Password} from "primereact/password";
import {ColorPicker} from "primereact/colorpicker";
import FileTypeGroupFilter from "../../../document/mimetype/FileTypeGroupFilter";
import FileTypeGroup from "../../../document/mimetype/FileTypeGroup";
import HighlighterLanguage from "../../parameter/HighlighterLanguage";
import FileInput, {File} from "../../../../shared/components/FileInput";
import {Checkbox} from "primereact/checkbox";
import DocumentSettings from "../../../document/DocumentSettings";
import validator from "validator";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import {PREVIEW_SIZE} from "../../../startscreen/Preview";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import DropdownNumber from "../../../../shared/components/DropdownNumber";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	AttachmentMode,
	Converter,
	ConverterHtml,
	ConverterHtmlErrorReport,
	ConverterHtmlErrorReportFormat,
	ConverterHtmlErrorReportMode,
	ConverterImage,
	ConverterMail,
	ConverterTemplate,
	ConverterText,
	ConvertPdfa,
	Environment,
	ExecutableStatus,
	ExecutableStatusState,
	Functions,
	Information,
	OfficeBridge,
	PdfaLevel,
	Settings,
	SettingsInterface,
	SharePointBridge,
	SignatureEditProfile,
	SyntaxHighlight,
	TemplateData,
	TemplateDataInterface,
	TextHighlight,
	TextWrapMode,
	XRechnungResources,
	XslTransformationFileFormat,
	XslTransformationResources
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {
	ConverterType,
	PortalConverter,
	TransformationSystemLanguage,
	XmlMode
} from "../../../../shared/models/PortalConverter";
import {PortalXslTransformationFile} from "../../../../shared/models/PortalXslTransformationFile";
import {PortalPage} from "../../../../shared/models/PortalPage";
import FormatEnum from "../../../../shared/models/FormatEnum";
import OrientationEnum from "../../../../shared/models/OrientationEnum";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import {Base64} from "js-base64";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import ConcurrentOperation from "../../../../shared/class/ConcurrentOperation";
import Exception from "../../../../shared/exception/Exception";
import {SelectItem, SelectItemOptionsType} from "primereact/selectitem";
import DropdownFilter from "../../../../shared/components/DropdownFilter";
import CodeInput, {CodeInputVariable} from "../../../../shared/components/CodeInput";
import Immutable from "../../../../shared/immutable/Immutable";
import {
	HeaderFooterAlignment,
	PortalConverterHeaderFooter
} from "../../../../shared/models/PortalConverterHeaderFooter";
import {Menu} from "primereact/menu";
import {PortalConverterHeaderFooterMargins} from "../../../../shared/models/PortalConverterHeaderFooterMargins";
import {PortalUrlConverterPage} from "../../../../shared/models/PortalUrlConverterPage";
import {PortalConverterHtml} from "../../../../shared/models/PortalConverterHtml";
import {Internal, InternalInterface} from "../../../../shared/models/Internal";
import MessageBox from "../../../../shared/components/MessageBox";

const NAMESPACE_STYLESHEET: string = "https://www.w3.org/1999/XSL/Transform";
const XML_TAG_STYLESHEET: string = "stylesheet";

export enum ConvertDialogLossless {
	ACTIVE_VALUE = 100,
	INACTIVE_VALUE = 90
}

interface ConvertDialogProperties extends PortalDialogProperties<PortalConverter> {
}

interface ConvertDialogStatus extends PortalDialogState<PortalConverter> {
	panel: number;
	pages: number;
	currentPage: string;
	hasPages: boolean;
	xsltFile: File;
	xrechnungFile: File;
	unit: LengthUnit;
	templateFile: File;
	errorReport?: ConverterHtmlErrorReportFormat;
	reportOpen: boolean;
}

export class ConvertDialogDefinition extends PortalDialog<PortalConverter, ConvertDialogProperties,
	ConvertDialogStatus> {
	private readonly PDFALEVELNONE: string = "";

	constructor(props: Readonly<ConvertDialogProperties>) {
		super(props);
		this.state = {
			...this.state,
			panel: 0,
			pages: 1,
			currentPage: "1",
			hasPages: false,
			xsltFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			xrechnungFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			fieldErrors: new Map<PortalConverter, string>(),
			unit: LengthUnit.MILLIMETER,
			templateFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			reportOpen: false,
		};
		this.onChange = this.onChange.bind(this);
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.converter?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.convertDocument, Functions.getConvertDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.CONVERT, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length >= 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [], [MimeTypes.PDF]);
	}

	protected renderError(error?: Exception): React.ReactNode {
		let exception: Exception | undefined = error || this.state.exception;

		if (!FunctionHelper.exists(exception)) {
			return super.renderError(error);
		}

		if (exception?.code === -12 && FunctionHelper.exists(this.state.errorReport)) {
			return (
				<div className="p-col-12">
					<MessageBox {...this.props} type={MessageType.DANGER} closeable={true}
								onClose={() => this.setState({
									exception: undefined,
									errorReport: undefined
								})}
								stacktrace={
									<pre>{exception!.message}</pre>
								}>
						The HTML document cannot be processed without errors. {exception.code}
					</MessageBox>
				</div>
			);
		}

		return super.renderError(error);
	}

	private renderGeneralTab(parameter: PortalConverter): ReactNode {
		let selection: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		let errors: Map<ConvertDialogFields, string> = this.state.fieldErrors;
		let portalInfo: Information = this.props.context.info;

		let mimeTypes: Array<MimeType> = [];
		let fileTypeGroups: Array<string> = [];
		for (let selectedDocument of selection) {
			mimeTypes.push(selectedDocument.getMimeType());
			fileTypeGroups.push(selectedDocument.getFileTypeGroup());
		}

		let pdfaLevel: string = this.PDFALEVELNONE;
		if (parameter.archive) {
			pdfaLevel = FunctionHelper.applyValue(
				parameter.pdfa?.convert?.level, ConvertPdfa.getLevelDefault()
			);
		}

		let converterTypeOptions: SelectItemOptionsType = [
			{
				label: Translate.value(
					"convert.general.convert.type.outsidein", {ns: "dialog"}
				),
				value: ConverterType.OUTSIDEIN
			}
		];

		let isOfficeEnabled: boolean = FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.converter?.executables?.office?.enabled,
			ExecutableStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.converter?.executables?.office?.status,
			ExecutableStatus.getStatusDefault()
		) === ExecutableStatusState.Ok;
		if (isOfficeEnabled && FileTypeGroupFilter.checkSelection(
			fileTypeGroups, [FileTypeGroup.OFFICE], []
		)) {
			converterTypeOptions.push(
				{
					label: Translate.value(
						"convert.general.convert.type.office_bridge", {ns: "dialog"}
					),
					value: ConverterType.OFFICE_BRIDGE
				}
			);
		}

		let isSharePointEnabled: boolean = FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.converter?.executables?.sharepoint?.enabled,
			ExecutableStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.converter?.executables?.sharepoint?.status,
			ExecutableStatus.getStatusDefault()
		) === ExecutableStatusState.Ok;
		if (isSharePointEnabled && FileTypeGroupFilter.checkSelection(
			fileTypeGroups, [FileTypeGroup.OFFICE], []
		)) {
			converterTypeOptions.push(
				{
					label: Translate.value(
						"convert.general.convert.type.sharepoint", {ns: "dialog"}
					),
					value: ConverterType.SHAREPOINT
				}
			);
		}

		let isSharepointOnlineEnabled: boolean = this.props.sharepointOnlineEnabled;
		if (isSharepointOnlineEnabled && FileTypeGroupFilter.checkSelection(
			fileTypeGroups, [FileTypeGroup.OFFICE], []
		)) {
			converterTypeOptions.push(
				{
					label: Translate.value(
						"convert.general.convert.type.sharepoint_online", {ns: "dialog"}
					),
					value: ConverterType.SHAREPOINT_ONLINE
				}
			);
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={converterTypeOptions.length <= 1}>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-converter-type">
							<Translate {...this.props} data="convert.general.convert.type.label" namespace="dialog"/>
							<Info {...this.props} data="convert.general.convert.type.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Dropdown inputId="control-converter-type" options={converterTypeOptions}
									  value={FunctionHelper.applyValue(
										  parameter.converterType, PortalConverter.getConverterTypeDefault()
									  )}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.CONVERTER, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.PDFA_FORMAT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-format">
							<Translate {...this.props} data="convert.general.convert.pdfa.format.label"
									   namespace="dialog"/>
							<Info {...this.props} data="convert.general.convert.pdfa.format.info"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Dropdown inputId="control-pdfa-format" value={pdfaLevel}
									  options={[
										  {
											  label: Translate.value(
												  "convert.general.convert.pdfa.format.none", {ns: "dialog"}
											  ),
											  value: this.PDFALEVELNONE
										  },
										  {
											  label: Translate.value(
												  "convert.general.convert.pdfa.format.1a", {ns: "dialog"}
											  ),
											  value: PdfaLevel._1a
										  },
										  {
											  label: Translate.value(
												  "convert.general.convert.pdfa.format.1b", {ns: "dialog"}
											  ),
											  value: PdfaLevel._1b
										  },
										  {
											  label: Translate.value(
												  "convert.general.convert.pdfa.format.2a", {ns: "dialog"}
											  ),
											  value: PdfaLevel._2a
										  },
										  {
											  label: Translate.value(
												  "convert.general.convert.pdfa.format.2b", {ns: "dialog"}
											  ),
											  value: PdfaLevel._2b
										  },
										  {
											  label: Translate.value(
												  "convert.general.convert.pdfa.format.2u", {ns: "dialog"}
											  ),
											  value: PdfaLevel._2u
										  },
										  {
											  label: Translate.value(
												  "convert.general.convert.pdfa.format.3a", {ns: "dialog"}
											  ),
											  value: PdfaLevel._3a
										  },
										  {
											  label: Translate.value(
												  "convert.general.convert.pdfa.format.3b", {ns: "dialog"}
											  ),
											  value: PdfaLevel._3b
										  },
										  {
											  label: Translate.value(
												  "convert.general.convert.pdfa.format.3u", {ns: "dialog"}
											  ),
											  value: PdfaLevel._3u
										  },
									  ]}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.PDFA_FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !parameter.archive ||
							 !this.state.fields?.contains(ConvertDialogFields.PDFA_RESOLUTION)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-resolution">
							<Translate {...this.props} data="convert.general.convert.pdfa.resolution.label"
									   namespace="dialog"/>
							<Info {...this.props} data="convert.general.convert.pdfa.resolution.info"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 slider-input">
							<InputNumber inputId="control-pdfa-resolution" step={1} suffix=" %" showButtons={true}
										 min={ConvertPdfa.getImageQualityMin()} useGrouping={false}
										 max={ConvertPdfa.getImageQualityMax()}
										 value={FunctionHelper.applyValue(
											 parameter.pdfa?.convert?.imageQuality, ConvertPdfa.getImageQualityDefault()
										 )}
										 onValueChange={(e) => this.onChange(
											 ConvertDialogFields.PDFA_RESOLUTION, e.value
										 )}
							/>
							<Slider min={ConvertPdfa.getImageQualityMin()} step={1}
									max={ConvertPdfa.getImageQualityMax()}
									value={FunctionHelper.applyValue(
										parameter.pdfa?.convert?.imageQuality, ConvertPdfa.getImageQualityDefault()
									)}
									onChange={(e) => this.onChange(
										ConvertDialogFields.PDFA_RESOLUTION, e.value
									)}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						MimeTypeFilter.checkSelection(mimeTypes, [
							MimeTypes.SVG, MimeTypes.EMF, MimeTypes.WMF, MimeTypes.POSTSCRIPT, MimeTypes.ACAD
						]) ||
						!this.state.fields?.contains(ConvertDialogFields.JPEG_QUALITY)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-jpeg-quality">
							<Translate {...this.props} data="convert.general.convert.jpeg_quality.label"
									   namespace="dialog"/>
							<Info {...this.props} data="convert.general.convert.jpeg_quality.info"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<span className="p-inputgroup-addon p-inputgroup-addon-checkbox slider-input">
									<Checkbox checked={parameter.jpegQuality !== ConvertDialogLossless.ACTIVE_VALUE}
											  onChange={() => this.onChange(
												  ConvertDialogFields.LOSSLESS,
												  parameter.jpegQuality !== ConvertDialogLossless.ACTIVE_VALUE
											  )}/>
								</span>
								<div className="slider-input">
									<InputNumber inputId="control-jpeg-quality" step={1} suffix=" %" showButtons={true}
												 value={FunctionHelper.applyValue(
													 parameter.jpegQuality, Converter.getJpegQualityDefault()
												 )}
												 min={Converter.getJpegQualityMin()} useGrouping={false}
												 max={Converter.getJpegQualityMax()}
												 disabled={parameter.jpegQuality === ConvertDialogLossless.ACTIVE_VALUE}
												 onValueChange={(e) => this.onChange(
													 ConvertDialogFields.JPEG_QUALITY, e.value
												 )}
									/>
									<Slider min={Converter.getJpegQualityMin()} step={1}
											max={Converter.getJpegQualityMax()}
											value={FunctionHelper.applyValue(
												parameter.jpegQuality, Converter.getJpegQualityDefault()
											)}
											disabled={parameter.jpegQuality === ConvertDialogLossless.ACTIVE_VALUE}
											onChange={(e) => this.onChange(
												ConvertDialogFields.JPEG_QUALITY, e.value
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						FileTypeGroupFilter.checkSelection(fileTypeGroups, [FileTypeGroup.IMAGE]) ||
						MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.IMAGE]) ||
						!this.state.fields?.contains(ConvertDialogFields.EMBED_FONTS)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-embed-fonts"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-embed-fonts"
										 checked={FunctionHelper.applyValue(
											 parameter.embedFonts, Converter.getEmbedFontsDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.EMBED_FONTS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-embed-fonts">
								<Translate {...this.props} data="convert.general.convert.embed_fonts.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.general.convert.embed_fonts.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					selection.length > 1 ||
					MimeTypeFilter.checkSelection(mimeTypes, [], [
						MimeTypes.XML, MimeTypes.APPXML, MimeTypes.APPXSLTXML
					])
				}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.XML_MODE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-xml-mode">
							<Translate {...this.props} data="convert.general.xml.mode.label" namespace="dialog"/>
							<Info {...this.props} data="convert.general.xml.mode.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-xml-mode"
									  value={FunctionHelper.applyValue(
										  parameter.xmlMode, PortalConverter.getXmlModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "convert.general.xml.mode.text", {ns: "dialog"}
											  ),
											  value: XmlMode.TEXT
										  },
										  {
											  label: Translate.value(
												  "convert.general.xml.mode.transform", {ns: "dialog"}
											  ),
											  value: XmlMode.TRANSFORM
										  },
										  {
											  label: Translate.value(
												  "convert.general.xml.mode.xrechnung", {ns: "dialog"}
											  ),
											  value: XmlMode.XRECHNUNG
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.XML_MODE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					!MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.HTML], [])
				}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HTML_PARSED)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-html-parsed"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-html-parsed"
										 checked={FunctionHelper.applyValue(
											 parameter.conversionAsHTML, PortalConverter.getConversionAsHTMLDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.HTML_PARSED, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-html-parsed">
								<Translate {...this.props} data="convert.general.html.parsed.label" namespace="dialog"/>
								<Info {...this.props} data="convert.general.html.parsed.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					!MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.HTML], []) ||
					!FunctionHelper.applyValue(
						parameter.conversionAsHTML, PortalConverter.getConversionAsHTMLDefault()
					)
				}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HTML_USE_AS_TEMPLATE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-html-use-as-template"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-html-use-as-template"
										 checked={FunctionHelper.applyValue(
											 parameter.html?.useAsTemplate, ConverterHtml.getUseAsTemplateDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.HTML_USE_AS_TEMPLATE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-html-use-as-template">
								<Translate {...this.props} data="convert.general.html.use_as_template.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.general.html.use_as_template.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					(
						!this.state.hasPages &&
						MimeTypeFilter.checkSelection(mimeTypes, [], [
							MimeTypes.PDF, MimeTypes.TIFF, MimeTypes.HEIF, MimeTypes.HEIC, MimeTypes.HEIF_SEQUENCE,
							MimeTypes.HEIC_SEQUENCE
						]) &&
						FileTypeGroupFilter.checkSelection(fileTypeGroups, [], [
							FileTypeGroup.PDF, FileTypeGroup.WORD, FileTypeGroup.OFFICE
						])
					) ||
					!this.state.fields?.contains(ConvertDialogFields.PAGERANGE)
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.PAGERANGE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pagerange">
							<Translate {...this.props} data="convert.general.pages.label" namespace="dialog"/>
							<Info {...this.props} data="convert.general.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							{FileTypeGroupFilter.checkSelection(
								fileTypeGroups, [FileTypeGroup.PDF], []
							) ?
								<PageRange inputId="control-pagerange"
										   className={errors.has(ConvertDialogFields.PAGERANGE) ? " p-invalid" : ""}
										   value={FunctionHelper.applyValue(parameter.pages, Converter.getPagesDefault())}
										   onChange={(e) => this.onChange(
											   ConvertDialogFields.PAGERANGE, e.value
										   )}
										   appendTo={document.getElementById("root")} editable={true}
										   addon={Translate.value(
											   "inputs.pagerange.of", {pages: this.state.pages}
										   )}
										   tooltip={errors.has(ConvertDialogFields.PAGERANGE) ? Translate.value(
											   errors.get(ConvertDialogFields.PAGERANGE)!, {ns: "message"}
										   ) : ""}
										   options={[
											   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
											   {
												   label: Translate.value("inputs.pagerange.current"),
												   value: this.state.currentPage
											   }
										   ]}/> :
								<PageRange inputId="control-pagerange"
										   className={errors.has(ConvertDialogFields.PAGERANGE) ? " p-invalid" : ""}
										   value={FunctionHelper.applyValue(parameter.pages, Converter.getPagesDefault())}
										   onChange={(e) => this.onChange(
											   ConvertDialogFields.PAGERANGE, e.value
										   )}
										   appendTo={document.getElementById("root")} editable={true}
										   tooltip={errors.has(ConvertDialogFields.PAGERANGE) ? Translate.value(
											   errors.get(ConvertDialogFields.PAGERANGE)!, {ns: "message"}
										   ) : ""}
										   options={[
											   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL}
										   ]}/>
							}
							{this.renderFieldError(errors.has(ConvertDialogFields.PAGERANGE) ? Translate.value(
								errors.get(ConvertDialogFields.PAGERANGE)!, {ns: "message"}
							) : "")}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private buildXsltFiles(xsltFiles: Array<PortalXslTransformationFile>): Array<ReactNode> {
		let xsltItems: Array<ReactNode> = [];

		let filecount: number = 0;
		for (let file of xsltFiles.entries()) {
			let fileIndex: number = file[0];
			let fileParameter: PortalXslTransformationFile = file[1];
			filecount++;

			xsltItems.push(
				<div className="xslt-item" key={"xslt-item-" + filecount}>
					<div className="xslt-name">{fileParameter.fileName}</div>
					<div className="xslt-functions">
						<div className="xslt-format">
							<Checkbox checked={fileParameter.format === XslTransformationFileFormat.Xslt}
									  inputId={fileParameter.id}
									  onChange={() => {
										  fileParameter.format =
											  fileParameter.format === XslTransformationFileFormat.Xslt ?
												  XslTransformationFileFormat.Other : XslTransformationFileFormat.Xslt;

										  this.setState({
											  parameter: this.state.parameter
										  });
									  }}/>
							<label htmlFor={fileParameter.id}>
								<Translate {...this.props} data="convert.specific.xml.xslt" namespace="dialog"/>
							</label>
						</div>
						<Button className="xslt-first" icon="icon icon-up"
								onClick={() => {
									let tempItem: PortalXslTransformationFile = xsltFiles[fileIndex - 1];
									xsltFiles[fileIndex - 1] = xsltFiles[fileIndex];
									xsltFiles[fileIndex] = tempItem;

									this.setState({
										parameter: this.state.parameter
									});
								}}/>
						<Button className="xslt-last" icon="icon icon-down"
								onClick={() => {
									let tempItem: PortalXslTransformationFile = xsltFiles[fileIndex + 1];
									xsltFiles[fileIndex + 1] = xsltFiles[fileIndex];
									xsltFiles[fileIndex] = tempItem;

									this.setState({
										parameter: this.state.parameter
									});
								}}/>
						<Button icon="icon icon-delete small"
								onClick={() => {
									xsltFiles.splice(fileIndex, 1);

									this.setState({
										parameter: this.state.parameter
									});
								}}/>
					</div>
				</div>
			);
		}

		return xsltItems;
	}

	private renderSpecificTab(parameter: PortalConverter): ReactNode {
		let selection: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		let errors: Map<ConvertDialogFields, string> = this.state.fieldErrors;

		let mimeTypes: Array<MimeType> = [];
		let fileTypeGroups: Array<string> = [];
		for (let selectedDocument of selection) {
			mimeTypes.push(selectedDocument.getMimeType());
			fileTypeGroups.push(selectedDocument.getFileTypeGroup());
		}

		let highlighterLanguages: Array<SelectItem> = [];
		for (let language of Object.values(HighlighterLanguage)) {
			highlighterLanguages.push({label: language, value: language});
		}

		let fonts: Array<SelectItem> = [
			{label: Translate.value("convert.specific.text.font.auto", {ns: "dialog"}), value: ""}
		];
		for (let font of FunctionHelper.applyValue(
			this.props.context.info.environment?.fonts, Environment.getFontsDefault()
		)) {
			fonts.push({label: font, value: font});
		}

		let xsltFiles: Array<PortalXslTransformationFile> = FunctionHelper.applyValue(
			parameter.transform?.xslTransformation?.resources?.file, XslTransformationResources.getFileDefault()
		);
		let xsltItems: Array<ReactNode> = this.buildXsltFiles(xsltFiles);

		let xrechnungFiles: Array<PortalXslTransformationFile> = FunctionHelper.applyValue(
			parameter.transform?.xRechnung?.visualization?.file, XRechnungResources.getFileDefault()
		);
		let xrechnungItems: Array<ReactNode> = this.buildXsltFiles(xrechnungFiles);

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={
					!(
						FunctionHelper.applyValue(
							this.props.context.info.webserviceStatus?.webservices?.converter?.executables?.chromium?.enabled,
							ExecutableStatus.getEnabledDefault()
						) &&
						selection.length === 1 &&
						(
							(
								FileTypeGroupFilter.checkSelection(fileTypeGroups, [FileTypeGroup.TEXT]) &&
								MimeTypeFilter.checkSelection(mimeTypes, [], [
									MimeTypes.XML, MimeTypes.APPXSLTXML, MimeTypes.APPXML
								])
							) ||
							(
								(FileTypeGroupFilter.checkSelection(fileTypeGroups, [FileTypeGroup.HTML]) ||
									MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.HTML])) &&
								!parameter.conversionAsHTML
							) ||
							(
								MimeTypeFilter.checkSelection(mimeTypes, [
									MimeTypes.XML, MimeTypes.APPXSLTXML, MimeTypes.APPXML
								]) &&
								!parameter.xRechnung && !parameter.xslTransformation
							)
						)
					)
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.SYNTAX_DETECTION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-syntax-detection"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-syntax-detection"
										 checked={FunctionHelper.applyValue(
											 parameter.text?.useSyntaxDetection,
											 ConverterText.getUseSyntaxDetectionDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.SYNTAX_DETECTION, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-syntax-detection">
								<Translate {...this.props} data="convert.specific.text.highlighter.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.text.highlighter.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						parameter.text?.useSyntaxDetection ||
						!this.state.fields?.contains(ConvertDialogFields.FONT_COLOR)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-text-color">
							<Translate {...this.props} data="convert.specific.text.color" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className={"p-inputgroup" + (
								errors.has(ConvertDialogFields.FONT_COLOR) ? " p-invalid" : ""
							)}>
								<InputText className={errors.has(ConvertDialogFields.FONT_COLOR) ? "p-invalid" : ""}
										   tooltip={errors.has(ConvertDialogFields.FONT_COLOR) ? Translate.value(
											   errors.get(ConvertDialogFields.FONT_COLOR)!, {ns: "message"}
										   ) : ""}
										   value={FunctionHelper.applyValue(
											   parameter.text?.textHighlight?.color, TextHighlight.getColorDefault()
										   )}
										   onInput={(e) => this.onChange(
											   ConvertDialogFields.FONT_COLOR, e.currentTarget.value
										   )}/>
								<ColorPicker inputId="control-text-color" className="p-inputgroup-addon" format="hex"
											 defaultColor={
												 TextHighlight.getColorDefault().replace("#", "")
											 }
											 value={FunctionHelper.applyValue(
												 parameter.text?.textHighlight?.color, TextHighlight.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.FONT_COLOR, "#" + e.value
											 )}
											 appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(ConvertDialogFields.FONT_COLOR) ? Translate.value(
								errors.get(ConvertDialogFields.FONT_COLOR)!, {ns: "message"}
							) : "")}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!parameter.text?.useSyntaxDetection ||
						!this.state.fields?.contains(ConvertDialogFields.LANGUAGE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-language">
							<Translate {...this.props} data="convert.specific.text.language" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-language" options={highlighterLanguages}
									  value={FunctionHelper.applyValue(
										  parameter.text?.syntaxHighlight?.language,
										  SyntaxHighlight.getLanguageDefault()
									  )}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.LANGUAGE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.FONT_FAMILY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-font-family">
							<Translate {...this.props} data="convert.specific.text.font.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<DropdownFilter inputId="control-font-family" options={fonts}
											value={
												parameter.text?.useSyntaxDetection ?
													FunctionHelper.applyValue(
														parameter.text?.syntaxHighlight?.fontFamily,
														SyntaxHighlight.getFontFamilyDefault()
													) :
													FunctionHelper.applyValue(
														parameter.text?.textHighlight?.fontFamily,
														TextHighlight.getFontFamilyDefault()
													)
											}
											onChange={(e) => this.onChange(
												ConvertDialogFields.FONT_FAMILY, e.value
											)}
											appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.WORD_BREAK)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-word-break">
							<Translate {...this.props} data="convert.specific.text.word_break.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-word-break"
									  options={
										  [
											  {
												  label: Translate.value(
													  "convert.specific.text.word_break.none", {ns: "dialog"}
												  ),
												  value: TextWrapMode.None
											  },
											  {
												  label: Translate.value(
													  "convert.specific.text.word_break.auto", {ns: "dialog"}
												  ),
												  value: TextWrapMode.Auto
											  },
											  {
												  label: Translate.value(
													  "convert.specific.text.word_break.word", {ns: "dialog"}
												  ),
												  value: TextWrapMode.Word
											  },
											  {
												  label: Translate.value(
													  "convert.specific.text.word_break.all", {ns: "dialog"}
												  ),
												  value: TextWrapMode.All
											  }
										  ]
									  }
									  value={
										  parameter.text?.useSyntaxDetection ?
											  FunctionHelper.applyValue(
												  parameter.text?.syntaxHighlight?.wordBreak,
												  SyntaxHighlight.getWordBreakDefault()
											  ) :
											  FunctionHelper.applyValue(
												  parameter.text?.textHighlight?.wordBreak,
												  TextHighlight.getWordBreakDefault()
											  )
									  }
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.WORD_BREAK, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.LINE_HEIGHT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-line-height">
							<Translate {...this.props} data="convert.specific.text.height" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-line-height" showButtons={true}
											value={
												parseInt(
													parameter.text?.useSyntaxDetection ?
														FunctionHelper.applyValue(
															parameter.text?.syntaxHighlight?.lineHeight,
															SyntaxHighlight.getLineHeightDefault()
														) :
														FunctionHelper.applyValue(
															parameter.text?.textHighlight?.lineHeight,
															TextHighlight.getLineHeightDefault()
														)
												)
											}
											onChange={(e) => this.onChange(
												ConvertDialogFields.LINE_HEIGHT, e.value
											)}
											appendTo={document.getElementById("root")}
											options={[
												{label: "8", value: 8},
												{label: "9", value: 9},
												{label: "10", value: 10},
												{label: "11", value: 11},
												{label: "12", value: 12},
												{label: "14", value: 14},
												{label: "16", value: 16},
												{label: "18", value: 18},
												{label: "20", value: 20},
												{label: "24", value: 24},
												{label: "28", value: 28},
												{label: "36", value: 36},
												{label: "48", value: 48},
												{label: "72", value: 72}
											]}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.FONT_SIZE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-font-size">
							<Translate {...this.props} data="convert.specific.text.size" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-font-size" className="p-col" showButtons={true}
											value={parseInt(
												parameter.text?.useSyntaxDetection ?
													FunctionHelper.applyValue(
														parameter.text?.syntaxHighlight?.fontSize,
														SyntaxHighlight.getFontSizeDefault()
													) :
													FunctionHelper.applyValue(
														parameter.text?.textHighlight?.fontSize,
														TextHighlight.getFontSizeDefault()
													)
											)}
											onChange={(e) => this.onChange(
												ConvertDialogFields.FONT_SIZE, e.value
											)}
											appendTo={document.getElementById("root")}
											options={[
												{label: "8", value: 8},
												{label: "9", value: 9},
												{label: "10", value: 10},
												{label: "11", value: 11},
												{label: "12", value: 12},
												{label: "14", value: 14},
												{label: "16", value: 16},
												{label: "18", value: 18},
												{label: "20", value: 20},
												{label: "24", value: 24},
												{label: "28", value: 28},
												{label: "36", value: 36},
												{label: "48", value: 48},
												{label: "72", value: 72}
											]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.LINE_NUMBERS)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-line-numbers"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-line-numbers"
										 checked={
											 parameter.text?.useSyntaxDetection ?
												 FunctionHelper.applyValue(
													 parameter.text?.syntaxHighlight?.lineNumbers,
													 SyntaxHighlight.getLineHeightDefault()
												 ) :
												 FunctionHelper.applyValue(
													 parameter.text?.textHighlight?.lineNumbers,
													 TextHighlight.getLineNumbersDefault()
												 )
										 }
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.LINE_NUMBERS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-line-numbers">
								<Translate {...this.props} data="convert.specific.text.line_numbers.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					FileTypeGroupFilter.checkSelection(fileTypeGroups, [], [FileTypeGroup.MAIL])
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.EMAIL_ATTACHMENT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-email-attachment">
							<Translate {...this.props} data="convert.specific.email.attachment.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-email-attachment"
									  value={FunctionHelper.applyValue(
										  parameter.mail?.attachmentMode, ConverterMail.getAttachmentModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "convert.specific.email.attachment.embed", {ns: "dialog"}
											  ),
											  value: AttachmentMode.Embed
										  },
										  {
											  label: Translate.value(
												  "convert.specific.email.attachment.remove", {ns: "dialog"}
											  ),
											  value: AttachmentMode.Remove
										  },
										  {
											  label: Translate.value(
												  "convert.specific.email.attachment.convert",
												  {ns: "dialog"}
											  ),
											  value: AttachmentMode.Convert
										  },
										  {
											  label: Translate.value(
												  "convert.specific.email.attachment.convert_merge",
												  {ns: "dialog"}
											  ),
											  value: AttachmentMode.ConvertMerge
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.EMAIL_ATTACHMENT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.EMAIL_ADJUST_FONTS)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-email-adjust-fonts"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-email-adjust-fonts"
										 checked={FunctionHelper.applyValue(
											 parameter.mail?.adjustFonts, ConverterMail.getAdjustFontsDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.EMAIL_ADJUST_FONTS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-email-adjust-fonts">
								<Translate {...this.props} data="convert.specific.email.adjust_font.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.email.adjust_font.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.EMAIL_DOWNLOAD_IMAGES)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-email-download-images"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-email-download-images"
										 checked={FunctionHelper.applyValue(
											 parameter.mail?.downloadImages, ConverterMail.getDownloadImagesDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.EMAIL_DOWNLOAD_IMAGES, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-email-download-images">
								<Translate {...this.props} data="convert.specific.email.download_images.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.email.download_images.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.EMAIL_USE_BACKGROUND)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-email-use-background"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-email-use-background"
										 checked={FunctionHelper.applyValue(
											 parameter.mail?.useBackground, ConverterMail.getUseBackgroundDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.EMAIL_USE_BACKGROUND, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-email-use-background">
								<Translate {...this.props} data="convert.specific.email.use_background.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.email.use_background.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.EMAIL_RESIZE_IMAGES)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-email-resize-images"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-email-resize-images"
										 checked={FunctionHelper.applyValue(
											 parameter.mail?.resizeImages, ConverterMail.getResizeImagesDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.EMAIL_RESIZE_IMAGES, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-email-resize-images">
								<Translate {...this.props} data="convert.specific.email.resize.images.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.email.resize.images.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.EMAIL_RESIZE_TABLES)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-email-resize-tables"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-email-resize-tables"
										 checked={FunctionHelper.applyValue(
											 parameter.mail?.resizeTables, ConverterMail.getResizeTablesDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.EMAIL_RESIZE_TABLES, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-email-resize-tables">
								<Translate {...this.props} data="convert.specific.email.resize.tables.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.email.resize.tables.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !this.state.fields?.contains(ConvertDialogFields.ASPECT_RATIO) ||
							 parameter.mail?.attachmentMode === AttachmentMode.Remove ||
							 parameter.mail?.attachmentMode === AttachmentMode.Embed
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-aspect-ratio"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-aspect-ratio"
										 checked={FunctionHelper.applyValue(
											 parameter.image?.maintainAspectRatio,
											 ConverterImage.getMaintainAspectRatioDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.ASPECT_RATIO, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-aspect-ratio">
								<Translate {...this.props} data="convert.specific.email.aspect_ratio.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.email.aspect_ratio.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					FileTypeGroupFilter.checkSelection(fileTypeGroups, [], [FileTypeGroup.MAIL])
				}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.RESOLUTION) ||
						parameter.mail?.attachmentMode === AttachmentMode.Remove ||
						parameter.mail?.attachmentMode === AttachmentMode.Embed
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-reduce-resolution"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-reduce-resolution"
										 checked={FunctionHelper.applyValue(
											 parameter.reduceResolution, Converter.getReduceResolutionDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.RESOLUTION, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-reduce-resolution">
								<Translate {...this.props} data="convert.specific.email.resolution.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.email.resolution.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!parameter.reduceResolution || !this.state.fields?.contains(ConvertDialogFields.DPI) ||
						parameter.mail?.attachmentMode === AttachmentMode.Remove ||
						parameter.mail?.attachmentMode === AttachmentMode.Embed
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-dpi">
							<Translate {...this.props} data="convert.specific.email.resolution.to" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-dpi" suffix={" " + ImageUnit.DPI} showButtons={true} step={1}
										 value={FunctionHelper.applyValue(parameter.dpi, Converter.getDpiDefault())}
										 min={Converter.getDpiMin()} max={Converter.getDpiMax()} useGrouping={false}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.DPI, e.value
										 )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					FileTypeGroupFilter.checkSelection(fileTypeGroups, [], [
						FileTypeGroup.MAIL, FileTypeGroup.VCARD, FileTypeGroup.VCAL
					])
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.TEMPLATE_LANGUAGE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-template-language">
							<Translate {...this.props} data="convert.specific.template.language.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-template-language"
									  value={FunctionHelper.applyValue(
										  parameter.template?.language, ConverterTemplate.getLanguageDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "convert.specific.template.language.server",
												  {ns: "dialog"}
											  ),
											  value: TransformationSystemLanguage
										  },
										  {
											  label: Translate.value(
												  "convert.specific.template.language.german",
												  {ns: "dialog"}
											  ),
											  value: "de"
										  },
										  {
											  label: Translate.value(
												  "convert.specific.template.language.english",
												  {ns: "dialog"}
											  ),
											  value: "en"
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.TEMPLATE_LANGUAGE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					!parameter.conversionAsHTML ||
					MimeTypeFilter.checkSelection(mimeTypes, [], [MimeTypes.HTML])
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.BASE_URL)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-base-url">
							<Translate {...this.props} data="convert.specific.html.baseurl.label" namespace="dialog"/>
							<Info {...this.props} data="convert.specific.html.baseurl.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-base-url"
									   value={FunctionHelper.applyValue(
										   parameter.html?.baseURL, ConverterHtml.getBaseURLDefault()
									   )}
									   onInput={(e) => this.onChange(
										   ConvertDialogFields.BASE_URL, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.HTML_ADJUST_FONTS)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-html-adjust-fonts"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-html-adjust-fonts"
										 checked={FunctionHelper.applyValue(
											 parameter.html?.adjustFonts, ConverterHtml.getAdjustFontsDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.HTML_ADJUST_FONTS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-html-adjust-fonts">
								<Translate {...this.props} data="convert.specific.html.adjust_font.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.html.adjust_font.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.HTML_DOWNLOAD_IMAGES)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-html-download-images"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-html-download-images"
										 checked={FunctionHelper.applyValue(
											 parameter.html?.downloadImages, ConverterHtml.getDownloadImagesDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.HTML_DOWNLOAD_IMAGES, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-html-download-images">
								<Translate {...this.props} data="convert.specific.html.download_images.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.html.download_images.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.HTML_USE_BACKGROUND)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-html-use-background"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-html-use-background"
										 checked={FunctionHelper.applyValue(
											 parameter.html?.useBackground, ConverterHtml.getUseBackgroundDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.HTML_USE_BACKGROUND, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-html-use-background">
								<Translate {...this.props} data="convert.specific.html.use_background.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.html.use_background.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					FileTypeGroupFilter.checkSelection(fileTypeGroups, [], [
						FileTypeGroup.OFFICE, FileTypeGroup.WORD
					])
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.PASSWORD)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-password">
							<Translate {...this.props} data="convert.specific.office.password.label"
									   namespace="dialog"/>
							<Info {...this.props} data="convert.specific.office.password.info"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Password feedback={false} id="control-password" toggleMask={true}
									  value={FunctionHelper.applyValue(
										  parameter.accessPassword, Converter.getAccessPasswordDefault()
									  )}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.PASSWORD, e.currentTarget.value
									  )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					FileTypeGroupFilter.checkSelection(fileTypeGroups, [], [
						FileTypeGroup.IMAGE, FileTypeGroup.WORD, FileTypeGroup.OFFICE
					]) &&
					MimeTypeFilter.checkSelection(mimeTypes, [], [
						MimeTypes.IMAGE, MimeTypes.RTF
					])
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.RESOLUTION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-reduce-resolution"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-reduce-resolution"
										 checked={FunctionHelper.applyValue(
											 parameter.reduceResolution, Converter.getReduceResolutionDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.RESOLUTION, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-reduce-resolution">
								<Translate {...this.props} data="convert.specific.image.resolution.label"
										   namespace="dialog"/>
								<Info {...this.props} data="convert.specific.image.resolution.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!parameter.reduceResolution || !this.state.fields?.contains(ConvertDialogFields.DPI)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-dpi">
							<Translate {...this.props} data="convert.specific.image.resolution.to" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-dpi" suffix={" " + ImageUnit.DPI} showButtons={true} step={1}
										 value={FunctionHelper.applyValue(parameter.dpi, Converter.getDpiDefault())}
										 min={Converter.getDpiMin()} max={Converter.getDpiMax()} useGrouping={false}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.DPI, e.value
										 )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					!parameter.xslTransformation ||
					MimeTypeFilter.checkSelection(mimeTypes, [], [
						MimeTypes.XML, MimeTypes.APPXML, MimeTypes.APPXSLTXML
					])
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.XSLT_FILE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-xslt-file">
							<Translate {...this.props} data="convert.specific.xml.files.label" namespace="dialog"/>
							<Info {...this.props} data="convert.specific.xml.files.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<FileInput inputId="control-xslt-file" accept="*/*" value={this.state.xsltFile.value}
									   filename={this.state.xsltFile.name}
									   onChange={(files) => this.onChange(ConvertDialogFields.XSLT_FILE, files)}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						xsltFiles.length < 1 || !this.state.fields?.contains(ConvertDialogFields.XSLT_FILE)
					}>
						<div className="xslt-list">
							<div className="xslt-header">
								<Translate {...this.props} data="convert.specific.xml.files.label" namespace="dialog"/>
							</div>
							<div className="xslt-items">
								{xsltItems}
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					!parameter.xRechnung ||
					MimeTypeFilter.checkSelection(mimeTypes, [], [
						MimeTypes.XML, MimeTypes.APPXML, MimeTypes.APPXSLTXML
					])
				}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.XRECHNUNG_OVERWRITE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-xrechnung-overwrite"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-xrechnung-overwrite"
										 checked={FunctionHelper.applyValue(
											 parameter.xRechnungOverwrite,
											 PortalConverter.getXRechnungOverwriteDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.XRECHNUNG_OVERWRITE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-xrechnung-overwrite">
								<Translate {...this.props} data="convert.specific.xml.design.label" namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!parameter.xRechnungOverwrite ||
						!this.state.fields?.contains(ConvertDialogFields.XRECHNUNG_FILE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-xrechnung-file">
							<Translate {...this.props} data="convert.specific.xml.files.label" namespace="dialog"/>
							<Info {...this.props} data="convert.specific.xml.files.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<FileInput inputId="control-xrechnung-file" accept="*/*"
									   value={this.state.xrechnungFile.value} filename={this.state.xrechnungFile.name}
									   onChange={(files) => this.onChange(
										   ConvertDialogFields.XRECHNUNG_FILE, files
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						!parameter.xRechnungOverwrite || xrechnungFiles.length < 1 ||
						!this.state.fields?.contains(ConvertDialogFields.XRECHNUNG_FILE)
					}>
						<div className="xslt-list">
							<div className="xslt-header">
								<Translate {...this.props} data="convert.specific.xml.files.label" namespace="dialog"/>
							</div>
							<div className="xslt-items">
								{xrechnungItems}
							</div>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderTemplateTab(parameter: PortalConverter): ReactNode {
		let htmlParameter: PortalConverterHtml = parameter.html as PortalConverterHtml;

		let errorReportMode: ConverterHtmlErrorReportMode = FunctionHelper.applyValue(
			htmlParameter.errorReport?.mode, ConverterHtmlErrorReport.getModeDefault()
		);

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HTML_ERROR_REPORT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-template-error-report">
							<Translate {...this.props} data="convert.template.error_report.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-template-error-report"
										 checked={errorReportMode === ConverterHtmlErrorReportMode.Message}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.HTML_ERROR_REPORT, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HTML_ERROR_REPORT_FORMAT) ||
						errorReportMode === ConverterHtmlErrorReportMode.None
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-template-error-report-format">
							<Translate {...this.props} data="convert.template.error_report.format.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Dropdown inputId="control-template-error-report-format"
									  value={FunctionHelper.applyValue(
										  htmlParameter.errorReport?.format, ConverterHtmlErrorReport.getFormatDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "convert.template.error_report.format.xml", {ns: "dialog"}
											  ),
											  value: ConverterHtmlErrorReportFormat.Xml
										  },
										  {
											  label: Translate.value(
												  "convert.template.error_report.format.json", {ns: "dialog"}
											  ),
											  value: ConverterHtmlErrorReportFormat.Json
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.HTML_ERROR_REPORT_FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HTML_TEMPLATE_USE_FILE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-template-use-file">
							<Translate {...this.props} data="convert.template.use_file" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-template-use-file"
										 checked={!FunctionHelper.applyValue(
											 htmlParameter.useTemplateFile,
											 PortalConverterHtml.getUseTemplateFileDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.HTML_TEMPLATE_USE_FILE, e.value
										 )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HTML_TEMPLATE_FILE) ||
						!FunctionHelper.applyValue(
							htmlParameter.useTemplateFile, PortalConverterHtml.getUseTemplateFileDefault()
						)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-template-file">
							<Translate {...this.props} data="convert.template.file" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<FileInput inputId="control-template-file" filename={this.state.templateFile.name}
									   value={this.state.templateFile.value} accept=".json"
									   onChange={(files) => this.onChange(
										   ConvertDialogFields.HTML_TEMPLATE_FILE, files[0]
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HTML_TEMPLATE_CONTENT) ||
						FunctionHelper.applyValue(
							htmlParameter.useTemplateFile, PortalConverterHtml.getUseTemplateFileDefault()
						)
					}>
						<label className="p-col-12 label-left" htmlFor="control-template-content">
							<Translate {...this.props} data="convert.template.content" namespace="dialog"/>
						</label>
						<div className="p-col-12">
							<CodeInput inputId="control-template-content" language={"json"} accept={".json"}
									   height={"400px"}
									   value={FunctionHelper.applyValue(
										   htmlParameter?.templateContent,
										   PortalConverterHtml.getTemplateContentDefault()
									   )}
									   options={{
										   contextmenu: false,
										   minimap: {
											   enabled: false
										   }
									   }}
									   onChange={(e) => this.onChange(
										   ConvertDialogFields.HTML_TEMPLATE_CONTENT, e.value
									   )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderHeaderTab(parameter: PortalConverter): ReactNode {
		let errors: Map<ConvertDialogFields, string> = this.state.fieldErrors;
		let menu: RefObject<Menu> = createRef<Menu>();

		const insertVariables: Immutable<Array<CodeInputVariable>> = Immutable.from([
			{
				label: Translate.value(
					"convert.template.variables.pagenumber", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.pageNumber}"
			},
			{
				label: Translate.value(
					"convert.template.variables.totalpages", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.totalPages}"
			},
			{
				label: Translate.value(
					"convert.template.variables.pagetitle", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.title}"
			}
		]);

		let fonts: Array<SelectItem> = [];
		for (let font of FunctionHelper.applyValue(
			this.props.context.info.environment?.fonts, Environment.getFontsDefault()
		)) {
			fonts.push({
				label: font,
				value: font
			});
		}

		let content = FunctionHelper.applyValue(
			parameter.header?.content, PortalConverterHeaderFooter.getContentDefault()
		);

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HEADER_PLAIN)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-plain">
							<Translate {...this.props} data="convert.template.plain" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-header-plain"
										 checked={!FunctionHelper.applyValue(
											 parameter.header?.plain, PortalConverterHeaderFooter.getPlainDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.HEADER_PLAIN, e.value
										 )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.header?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.HEADER_FONT_FAMILY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-font-family">
							<Translate {...this.props} data="convert.template.font.family" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<DropdownFilter inputId="control-header-font-family"
											value={FunctionHelper.applyValue(
												parameter.header?.fontFamily,
												PortalConverterHeaderFooter.getFontFamilyDefault()
											)}
											onChange={(e) => this.onChange(
												ConvertDialogFields.HEADER_FONT_FAMILY, e.value
											)}
											onClear={() => this.onChange(
												ConvertDialogFields.HEADER_FONT_FAMILY, ""
											)}
											appendTo={document.getElementById("root")} options={fonts}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.HEADER_FONT_COLOR)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-font-color">
							<Translate {...this.props} data="convert.template.font.color" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className={"p-inputgroup" + (
								errors.has(ConvertDialogFields.HEADER_FONT_COLOR) ? " p-invalid" : ""
							)}>
								<InputText
									className={errors.has(
										ConvertDialogFields.HEADER_FONT_COLOR
									) ? "p-invalid" : ""}
									tooltip={errors.has(
										ConvertDialogFields.HEADER_FONT_COLOR
									) ? Translate.value(
										errors.get(ConvertDialogFields.HEADER_FONT_COLOR)!,
										{ns: "message"}
									) : ""}
									value={FunctionHelper.applyValue(
										parameter.header?.color, PortalConverterHeaderFooter.getColorDefault()
									)}
									onInput={(e) => this.onChange(
										ConvertDialogFields.HEADER_FONT_COLOR, e.currentTarget.value
									)}/>
								<ColorPicker inputId="control-header-font-color" className="p-inputgroup-addon"
											 format="hex"
											 defaultColor={PortalConverterHeaderFooter.getColorDefault().replace(
												 "#", ""
											 )}
											 value={FunctionHelper.applyValue(
												 parameter.header?.color, PortalConverterHeaderFooter.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.HEADER_FONT_COLOR, "#" + e.value
											 )} appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(ConvertDialogFields.HEADER_FONT_COLOR) ?
								Translate.value(
									errors.get(ConvertDialogFields.HEADER_FONT_COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.HEADER_FONT_SIZE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-font-size">
							<Translate {...this.props} data="convert.template.font.size" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-header-font-size" showButtons={true}
											value={FunctionHelper.applyValue(
												parameter.header?.fontSize,
												PortalConverterHeaderFooter.getFontSizeDefault()
											)}
											onChange={(e) => this.onChange(
												ConvertDialogFields.HEADER_FONT_SIZE, e.value
											)}
											min={PortalConverterHeaderFooter.getFontSizeMin()}
											max={PortalConverterHeaderFooter.getFontSizeMax()}
											appendTo={document.getElementById("root")}
											options={[
												{label: "8", value: 8},
												{label: "9", value: 9},
												{label: "10", value: 10},
												{label: "11", value: 11},
												{label: "12", value: 12},
												{label: "14", value: 14},
												{label: "16", value: 16},
												{label: "18", value: 18},
												{label: "20", value: 20},
												{label: "24", value: 24},
												{label: "28", value: 28},
												{label: "36", value: 36},
												{label: "48", value: 48},
												{label: "72", value: 72}
											]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HEADER_ALIGNMENT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-alignment">
							<Translate {...this.props} data="convert.template.font.alignment.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Dropdown inputId="control-header-alignment"
									  value={FunctionHelper.applyValue(
										  parameter.header?.alignment, PortalConverterHeaderFooter.getAlignmentDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "convert.template.font.alignment.left", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.LEFT
										  },
										  {
											  label: Translate.value(
												  "convert.template.font.alignment.center", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.CENTER
										  },
										  {
											  label: Translate.value(
												  "convert.template.font.alignment.right", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.RIGHT
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.HEADER_ALIGNMENT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.header?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HEADER_MARGIN_LEFT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-margin-left">
							<Translate {...this.props} data="convert.template.margin.left" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-header-margin-left" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.header?.margins?.left,
													 PortalConverterHeaderFooterMargins.getLeftDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.HEADER_MARGIN_LEFT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												ConvertDialogFields.HEADER_MARGIN_LEFT, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HEADER_MARGIN_RIGHT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-margin-right">
							<Translate {...this.props} data="convert.template.margin.right" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-header-margin-right" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.header?.margins?.right,
													 PortalConverterHeaderFooterMargins.getRightDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.HEADER_MARGIN_RIGHT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												ConvertDialogFields.HEADER_MARGIN_RIGHT, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HEADER_MARGIN_TOP)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-margin-top">
							<Translate {...this.props} data="convert.template.margin.top" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-header-margin-top" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.header?.margins?.top,
													 PortalConverterHeaderFooterMargins.getTopDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.HEADER_MARGIN_TOP, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												ConvertDialogFields.HEADER_MARGIN_TOP, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.header?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HEADER_CONTENT)
					}>
						<label className="p-col-12 large-label" htmlFor="control-header-content">
							<Translate {...this.props} data="convert.template.content" namespace="dialog"/>
						</label>
						<div className="p-col-12 large-input p-grid">
							<div className="p-inputgroup">
								<InputText id="control-header-content"
										   value={FunctionHelper.applyValue(
											   parameter.header?.content,
											   PortalConverterHeaderFooter.getContentDefault()
										   )}
										   onInput={(e) => this.onChange(
											   ConvertDialogFields.HEADER_CONTENT, e.currentTarget.value
										   )}/>
								<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
										onClick={() => this.onChange(
											ConvertDialogFields.HEADER_CONTENT, ""
										)}/>
								<Button icon="icon icon-angle-down" aria-controls={"control-header-content-menu"}
										onClick={(event) => menu.current!.toggle(event)}
										tooltip={Translate.value("inputs.code.variables")}
										className="p-inputgroup-addon" aria-haspopup={true}/>
								<Menu id="control-header-content-menu" popup={true} ref={menu}
									  appendTo={document.getElementById("root")}
									  model={[
										  {
											  label: Translate.value(
												  "convert.template.variables.pagenumber", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  ConvertDialogFields.HEADER_CONTENT,
												  content + "${html.pageNumber}"
											  )
										  },
										  {
											  label: Translate.value(
												  "convert.template.variables.totalpages", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  ConvertDialogFields.HEADER_CONTENT,
												  content + "${html.totalPages}"
											  )
										  },
										  {
											  label: Translate.value(
												  "convert.template.variables.pagetitle", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  ConvertDialogFields.HEADER_CONTENT,
												  content + "${html.title}"
											  )
										  }
									  ]}/>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={FunctionHelper.applyValue(
					parameter.header?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.HEADER_CONTENT)
					}>
						<label className="p-col-12 label-left" htmlFor="control-header-html">
							<Translate {...this.props} data="convert.template.content" namespace="dialog"/>
						</label>
						<div className="p-col-12">
							<CodeInput inputId="control-header-html" language={"html"} accept={".html"} height={"400px"}
									   value={FunctionHelper.applyValue(
										   parameter.header?.content, PortalConverterHeaderFooter.getContentDefault()
									   )}
									   variables={insertVariables}
									   options={{
										   contextmenu: false,
										   minimap: {
											   enabled: false
										   }
									   }}
									   onChange={(e) => this.onChange(
										   ConvertDialogFields.HEADER_CONTENT, e.value
									   )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderFooterTab(parameter: PortalConverter): ReactNode {
		let errors: Map<ConvertDialogFields, string> = this.state.fieldErrors;
		let menu: RefObject<Menu> = createRef<Menu>();

		const insertVariables: Immutable<Array<CodeInputVariable>> = Immutable.from([
			{
				label: Translate.value(
					"convert.template.variables.pagenumber", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.pageNumber}"
			},
			{
				label: Translate.value(
					"convert.template.variables.totalpages", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.totalPages}"
			},
			{
				label: Translate.value(
					"convert.template.variables.pagetitle", {ns: "dialog"}
				),
				/* eslint-disable no-template-curly-in-string */
				value: "${html.title}"
			}
		]);

		let fonts: Array<SelectItem> = [];
		for (let font of FunctionHelper.applyValue(
			this.props.context.info.environment?.fonts, Environment.getFontsDefault()
		)) {
			fonts.push({
				label: font,
				value: font
			});
		}

		let content = FunctionHelper.applyValue(
			parameter.footer?.content, PortalConverterHeaderFooter.getContentDefault()
		);

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.FOOTER_PLAIN)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-plain">
							<Translate {...this.props} data="convert.template.plain" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-footer-plain"
										 checked={!FunctionHelper.applyValue(
											 parameter.footer?.plain, PortalConverterHeaderFooter.getPlainDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.FOOTER_PLAIN, e.value
										 )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.footer?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.FOOTER_FONT_FAMILY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-font-family">
							<Translate {...this.props} data="convert.template.font.family" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<DropdownFilter inputId="control-footer-font-family"
											value={FunctionHelper.applyValue(
												parameter.footer?.fontFamily,
												PortalConverterHeaderFooter.getFontFamilyDefault()
											)}
											onChange={(e) => this.onChange(
												ConvertDialogFields.FOOTER_FONT_FAMILY, e.value
											)}
											onClear={() => this.onChange(
												ConvertDialogFields.FOOTER_FONT_FAMILY, ""
											)}
											appendTo={document.getElementById("root")} options={fonts}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.FOOTER_FONT_COLOR)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-font-color">
							<Translate {...this.props} data="convert.template.font.color" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className={"p-inputgroup" + (
								errors.has(ConvertDialogFields.FOOTER_FONT_COLOR) ? " p-invalid" : ""
							)}>
								<InputText
									className={errors.has(
										ConvertDialogFields.FOOTER_FONT_COLOR
									) ? "p-invalid" : ""}
									tooltip={errors.has(
										ConvertDialogFields.FOOTER_FONT_COLOR
									) ? Translate.value(
										errors.get(ConvertDialogFields.FOOTER_FONT_COLOR)!,
										{ns: "message"}
									) : ""}
									value={FunctionHelper.applyValue(
										parameter.footer?.color, PortalConverterHeaderFooter.getColorDefault()
									)}
									onInput={(e) => this.onChange(
										ConvertDialogFields.FOOTER_FONT_COLOR, e.currentTarget.value
									)}/>
								<ColorPicker inputId="control-footer-font-color" className="p-inputgroup-addon"
											 format="hex"
											 defaultColor={PortalConverterHeaderFooter.getColorDefault().replace(
												 "#", ""
											 )}
											 value={FunctionHelper.applyValue(
												 parameter.footer?.color, PortalConverterHeaderFooter.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.FOOTER_FONT_COLOR, "#" + e.value
											 )} appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(ConvertDialogFields.FOOTER_FONT_COLOR) ?
								Translate.value(
									errors.get(ConvertDialogFields.FOOTER_FONT_COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.FOOTER_FONT_SIZE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-font-size">
							<Translate {...this.props} data="convert.template.font.size" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-footer-font-size" showButtons={true}
											value={FunctionHelper.applyValue(
												parameter.footer?.fontSize,
												PortalConverterHeaderFooter.getFontSizeDefault()
											)}
											onChange={(e) => this.onChange(
												ConvertDialogFields.FOOTER_FONT_SIZE, e.value
											)}
											min={PortalConverterHeaderFooter.getFontSizeMin()}
											max={PortalConverterHeaderFooter.getFontSizeMax()}
											appendTo={document.getElementById("root")}
											options={[
												{label: "8", value: 8},
												{label: "9", value: 9},
												{label: "10", value: 10},
												{label: "11", value: 11},
												{label: "12", value: 12},
												{label: "14", value: 14},
												{label: "16", value: 16},
												{label: "18", value: 18},
												{label: "20", value: 20},
												{label: "24", value: 24},
												{label: "28", value: 28},
												{label: "36", value: 36},
												{label: "48", value: 48},
												{label: "72", value: 72}
											]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.FOOTER_ALIGNMENT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-alignment">
							<Translate {...this.props} data="convert.template.font.alignment.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Dropdown inputId="control-footer-alignment"
									  value={FunctionHelper.applyValue(
										  parameter.footer?.alignment, PortalConverterHeaderFooter.getAlignmentDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "convert.template.font.alignment.left", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.LEFT
										  },
										  {
											  label: Translate.value(
												  "convert.template.font.alignment.center", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.CENTER
										  },
										  {
											  label: Translate.value(
												  "convert.template.font.alignment.right", {ns: "dialog"}
											  ),
											  value: HeaderFooterAlignment.RIGHT
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.FOOTER_ALIGNMENT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.footer?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.FOOTER_MARGIN_LEFT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-margin-left">
							<Translate {...this.props} data="convert.template.margin.left" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-footer-margin-left" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.footer?.margins?.left,
													 PortalConverterHeaderFooterMargins.getLeftDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.FOOTER_MARGIN_LEFT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												ConvertDialogFields.FOOTER_MARGIN_LEFT, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.FOOTER_MARGIN_RIGHT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-margin-right">
							<Translate {...this.props} data="convert.template.margin.right" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-footer-margin-right" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.footer?.margins?.right,
													 PortalConverterHeaderFooterMargins.getRightDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.FOOTER_MARGIN_RIGHT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												ConvertDialogFields.FOOTER_MARGIN_RIGHT, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.FOOTER_MARGIN_BOTTOM)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-margin-bottom">
							<Translate {...this.props} data="convert.template.margin.bottom" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-footer-margin-bottom" step={1} showButtons={true}
											 suffix={" " + this.state.unit} maxFractionDigits={2} minFractionDigits={2}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.footer?.margins?.bottom,
													 PortalConverterHeaderFooterMargins.getBottomDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.FOOTER_MARGIN_BOTTOM, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												ConvertDialogFields.FOOTER_MARGIN_BOTTOM, 0
											)}/>
								</div>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={!FunctionHelper.applyValue(
					parameter.footer?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.FOOTER_CONTENT)
					}>
						<label className="p-col-12 large-label" htmlFor="control-footer-content">
							<Translate {...this.props} data="convert.template.content" namespace="dialog"/>
						</label>
						<div className="p-col-12 large-input p-grid">
							<div className="p-inputgroup">
								<InputText id="control-footer-content"
										   value={FunctionHelper.applyValue(
											   parameter.footer?.content,
											   PortalConverterHeaderFooter.getContentDefault()
										   )}
										   onInput={(e) => this.onChange(
											   ConvertDialogFields.FOOTER_CONTENT, e.currentTarget.value
										   )}/>
								<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
										onClick={() => this.onChange(
											ConvertDialogFields.FOOTER_CONTENT, ""
										)}/>
								<Button icon="icon icon-angle-down" aria-controls={"control-footer-content-menu"}
										onClick={(event) => menu.current!.toggle(event)}
										tooltip={Translate.value("inputs.code.variables")}
										className="p-inputgroup-addon" aria-haspopup={true}/>
								<Menu id="control-footer-content-menu" popup={true} ref={menu}
									  appendTo={document.getElementById("root")}
									  model={[
										  {
											  label: Translate.value(
												  "convert.template.variables.pagenumber", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  ConvertDialogFields.FOOTER_CONTENT,
												  content + "${html.pageNumber}"
											  )
										  },
										  {
											  label: Translate.value(
												  "convert.template.variables.totalpages", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  ConvertDialogFields.FOOTER_CONTENT,
												  content + "${html.totalPages}"
											  )
										  },
										  {
											  label: Translate.value(
												  "convert.template.variables.pagetitle", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  ConvertDialogFields.FOOTER_CONTENT,
												  content + "${html.title}"
											  )
										  }
									  ]}/>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={FunctionHelper.applyValue(
					parameter.footer?.plain, PortalConverterHeaderFooter.getPlainDefault()
				)}>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(ConvertDialogFields.FOOTER_CONTENT)
					}>
						<label className="p-col-12 label-left" htmlFor="control-footer-html">
							<Translate {...this.props} data="convert.template.content" namespace="dialog"/>
						</label>
						<div className="p-col-12">
							<CodeInput inputId="control-footer-html" language={"html"} accept={".html"} height={"400px"}
									   value={FunctionHelper.applyValue(
										   parameter.footer?.content, PortalConverterHeaderFooter.getContentDefault()
									   )}
									   variables={insertVariables}
									   options={{
										   contextmenu: false,
										   minimap: {
											   enabled: false
										   }
									   }}
									   onChange={(e) => this.onChange(
										   ConvertDialogFields.FOOTER_CONTENT, e.value
									   )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderFormatTab(parameter: PortalConverter): ReactNode {
		let pageParameter: PortalPage | undefined = parameter.page as PortalPage;
		let selection: Array<PortalDocument> = this.props.context.documentManager.getSelection();

		let mimeTypes: Array<MimeType> = [];
		let fileTypeGroups: Array<string> = [];
		for (let selectedDocument of selection) {
			mimeTypes.push(selectedDocument.getMimeType());
			fileTypeGroups.push(selectedDocument.getFileTypeGroup());
		}

		if (!FunctionHelper.exists(pageParameter)) {
			return null;
		}

		let pageFormats: Array<any> = [
			{
				label: Translate.value("convert.format.size.custom", {ns: "dialog"}),
				value: FormatEnum.CUSTOM
			}
		];

		if (
			FileTypeGroupFilter.checkSelection(fileTypeGroups, [FileTypeGroup.OFFICE]) ||
			MimeTypeFilter.checkSelection(mimeTypes, [
				MimeTypes.IMAGE
			], [
				MimeTypes.SVG, MimeTypes.EMF, MimeTypes.WMF
			])
		) {
			pageFormats.push(
				{
					label: Translate.value("convert.format.size.original", {ns: "dialog"}),
					value: FormatEnum.ORIGINAL
				}
			);
		}

		for (let formatGroup of paperSizes) {
			pageFormats.push({
				label: Translate.value("papersize." + formatGroup.name),
				className: "group",
				value: formatGroup.name,
				disabled: true
			});

			for (let paperFormat of formatGroup.values) {
				pageFormats.push({label: paperFormat.name, className: "item", value: paperFormat.name});
			}
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(ConvertDialogFields.PAGE_FORMAT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-page-format">
							<Translate {...this.props} data="convert.format.size.label" namespace="dialog"/>
							<Info {...this.props} data="convert.format.size.info" options={{ns: "dialog"}}
								  markdown={true}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-page-format" options={pageFormats}
									  value={FunctionHelper.applyValue(
										  pageParameter.format, PortalPage.getFormatDefault()
									  )}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.PAGE_FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format === FormatEnum.ORIGINAL ||
						!this.state.fields?.contains(ConvertDialogFields.UNIT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-unit">
							<Translate {...this.props} data="convert.format.unit" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Dropdown inputId="control-unit"
									  value={FunctionHelper.applyValue(pageParameter.unit, PortalPage.getUnitDefault())}
									  options={[
										  {label: Translate.value("inputs.unit.pt"), value: LengthUnit.POINTS},
										  {label: Translate.value("inputs.unit.pc"), value: LengthUnit.PICA},
										  {label: Translate.value("inputs.unit.mm"), value: LengthUnit.MILLIMETER},
										  {label: Translate.value("inputs.unit.in"), value: LengthUnit.INCHES},
									  ]}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.UNIT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format !== FormatEnum.CUSTOM ||
						!this.state.fields?.contains(ConvertDialogFields.WIDTH)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-width">
							<Translate {...this.props} data="convert.format.width" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7"
							 hidden={!this.state.fields?.contains(ConvertDialogFields.WIDTH)}>
							<InputNumber inputId="control-width" step={1} suffix={" " + this.state.unit}
										 maxFractionDigits={2} minFractionDigits={2} min={0} showButtons={true}
										 useGrouping={false}
										 value={UnitHelper.unitToUnit(
											 FunctionHelper.applyValue(
												 pageParameter.width, PortalUrlConverterPage.getWidthDefault()
											 ),
											 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.WIDTH, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format !== FormatEnum.CUSTOM ||
						!this.state.fields?.contains(ConvertDialogFields.HEIGHT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-height">
							<Translate {...this.props} data="convert.format.height" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7"
							 hidden={!this.state.fields?.contains(ConvertDialogFields.HEIGHT)}>
							<InputNumber inputId="control-height" step={1} suffix={" " + this.state.unit}
										 maxFractionDigits={2} minFractionDigits={2} min={0} showButtons={true}
										 useGrouping={false}
										 value={UnitHelper.unitToUnit(
											 FunctionHelper.applyValue(
												 pageParameter.height, PortalUrlConverterPage.getHeightDefault()
											 ),
											 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.HEIGHT, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 pageParameter.format === FormatEnum.ORIGINAL ||
							 pageParameter.format === FormatEnum.CUSTOM ||
							 !this.state.fields?.contains(ConvertDialogFields.ORIENTATION)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-orientation">
							<Translate {...this.props} data="convert.format.orientation.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-orientation"
									  value={FunctionHelper.applyValue(
										  pageParameter.orientation, PortalPage.getOrientationDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "convert.format.orientation.vertical", {ns: "dialog"}
											  ),
											  value: OrientationEnum.VERTICAL
										  },
										  {
											  label: Translate.value(
												  "convert.format.orientation.horizontal", {ns: "dialog"}
											  ),
											  value: OrientationEnum.HORIZONTAL
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ConvertDialogFields.ORIENTATION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 FileTypeGroupFilter.checkSelection(fileTypeGroups, [FileTypeGroup.MAIL], []) ||
							 pageParameter.format === FormatEnum.ORIGINAL ||
							 !this.state.fields?.contains(ConvertDialogFields.ASPECT_RATIO)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-aspect-ratio"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-aspect-ratio"
										 checked={FunctionHelper.applyValue(
											 parameter.image?.maintainAspectRatio,
											 ConverterImage.getMaintainAspectRatioDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ConvertDialogFields.ASPECT_RATIO, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-aspect-ratio">
								<Translate {...this.props} data="convert.format.aspect_ratio" namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={pageParameter.format === FormatEnum.ORIGINAL}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format === FormatEnum.ORIGINAL ||
						!this.state.fields?.contains(ConvertDialogFields.MARGIN_TOP)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-top">
							<Translate {...this.props} data="convert.format.margin.top" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-top" step={1} suffix={" " + this.state.unit}
											 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 pageParameter.top, PortalUrlConverterPage.getTopDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.MARGIN_TOP, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(ConvertDialogFields.MARGIN_TOP, 0)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format === FormatEnum.ORIGINAL ||
						!this.state.fields?.contains(ConvertDialogFields.MARGIN_BOTTOM)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-bottom">
							<Translate {...this.props} data="convert.format.margin.bottom" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-bottom" step={1} suffix={" " + this.state.unit}
											 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 pageParameter.bottom, PortalUrlConverterPage.getBottomDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.MARGIN_BOTTOM, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(ConvertDialogFields.MARGIN_BOTTOM, 0)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format === FormatEnum.ORIGINAL ||
						!this.state.fields?.contains(ConvertDialogFields.MARGIN_LEFT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-left">
							<Translate {...this.props} data="convert.format.margin.left" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-left" step={1} suffix={" " + this.state.unit}
											 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 pageParameter.left, PortalUrlConverterPage.getLeftDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.MARGIN_LEFT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(ConvertDialogFields.MARGIN_LEFT, 0)}/>
								</div>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format === FormatEnum.ORIGINAL ||
						!this.state.fields?.contains(ConvertDialogFields.MARGIN_RIGHT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-right">
							<Translate {...this.props} data="convert.format.margin.right" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-right" step={1} suffix={" " + this.state.unit}
											 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 pageParameter.right, PortalUrlConverterPage.getRightDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 ConvertDialogFields.MARGIN_RIGHT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(ConvertDialogFields.MARGIN_RIGHT, 0)}/>
								</div>
							</div>
						</div>
					</div>
				</fieldset>

				{this.renderPreview(pageParameter)}
			</>
		);
	}

	public renderPreview(parameter: PortalPage): ReactNode {
		let scale: number = Math.max(parameter.width!, parameter.height!) / PREVIEW_SIZE;

		let pageStyle: CSSProperties = {
			width: (parameter.width! / scale) + LengthUnit.PIXEL,
			height: (parameter.height! / scale) + LengthUnit.PIXEL
		};

		return (
			<fieldset className="p-col-12 p-grid"
					  hidden={
						  parameter.format === FormatEnum.ORIGINAL ||
						  !this.state.fields?.contains(ConvertDialogFields.PREVIEW)
					  }>
				<div className="p-col-12 p-grid">
					<div className="page-preview">
						<div style={pageStyle}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalConverter | undefined = this.state.parameter?.data as PortalConverter;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let selection: Array<PortalDocument> = this.props.context.documentManager.getSelection();

		let mimeTypes: Array<MimeType> = [];
		let fileTypeGroups: Array<string> = [];
		for (let selectedDocument of selection) {
			mimeTypes.push(selectedDocument.getMimeType());
			fileTypeGroups.push(selectedDocument.getFileTypeGroup());
		}

		let hasHeaderFooterSettings: boolean =
			FileTypeGroupFilter.checkSelection(fileTypeGroups, [FileTypeGroup.TEXT, FileTypeGroup.MAIL]) ||
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.HTML]);

		let hasTemplateSettings: boolean = MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.HTML], []) &&
			parameter.conversionAsHTML && FunctionHelper.applyValue(
				parameter.html?.useAsTemplate, ConverterHtml.getUseAsTemplateDefault()
			);

		let hasSpecificSettings: boolean =
			FileTypeGroupFilter.checkSelection(fileTypeGroups, [
				FileTypeGroup.TEXT, FileTypeGroup.MAIL, FileTypeGroup.OFFICE, FileTypeGroup.WORD
			]) ||
			MimeTypeFilter.checkSelection(mimeTypes, [
				MimeTypes.XML, MimeTypes.APPXML, MimeTypes.APPXSLTXML, MimeTypes.HTML, MimeTypes.IMAGE, MimeTypes.RTF
			], [MimeTypes.SVG]);

		return (
			<TabView activeIndex={this.state.panel} onTabChange={
				(e) => this.setState({panel: e.index})
			}>
				<TabPanel header={Translate.value("convert.general.label", {ns: "dialog"})}
						  headerClassName="control-tab-general" contentClassName="general-tab-panel">
					{this.renderGeneralTab(parameter)}
				</TabPanel>
				<TabPanel header={Translate.value("convert.specific.label", {ns: "dialog"})}
						  headerStyle={hasSpecificSettings ? {} : {display: "none"}}
						  headerClassName="control-tab-specific" contentClassName="specific-tab-panel">
					{hasSpecificSettings && this.renderSpecificTab(parameter)}
				</TabPanel>
				<TabPanel header={Translate.value("convert.template.html", {ns: "dialog"})}
						  headerStyle={hasTemplateSettings ? {} : {display: "none"}}
						  headerClassName="control-tab-template" contentClassName="template-tab-panel">
					{hasTemplateSettings && this.renderTemplateTab(parameter)}
				</TabPanel>
				<TabPanel header={Translate.value("convert.template.header", {ns: "dialog"})}
						  headerStyle={hasHeaderFooterSettings ? {} : {display: "none"}}
						  headerClassName="control-tab-header" contentClassName="header-tab-panel">
					{hasHeaderFooterSettings && this.renderHeaderTab(parameter)}
				</TabPanel>
				<TabPanel header={Translate.value("convert.template.footer", {ns: "dialog"})}
						  headerStyle={hasHeaderFooterSettings ? {} : {display: "none"}}
						  headerClassName="control-tab-footer" contentClassName="footer-tab-panel">
					{hasHeaderFooterSettings && this.renderFooterTab(parameter)}
				</TabPanel>
				<TabPanel header={Translate.value("convert.format.label", {ns: "dialog"})}
						  headerClassName="control-tab-format" contentClassName="format-tab-panel">
					{this.renderFormatTab(parameter)}
				</TabPanel>
			</TabView>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["convert-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				{this.renderError()}
				<Helper {...this.props}>
					<Translate {...this.props} data={"dialog.convert.general"} namespace={"help"} markdown={true}/>
				</Helper>
				<Helper {...this.props} type={MessageType.WARNING}>
					<Translate {...this.props} data={"dialog.convert.info"} namespace={"help"} markdown={true}/>
				</Helper>
				{this.renderContents()}
			</Dialog>
		);
	}

	private convertContents(parameter: PortalConverterHeaderFooter): string {
		if (!parameter.plain) {
			return "";
		}

		let style: Array<string> = [
			"font-size:" + parameter.fontSize! + LengthUnit.POINTS,
			"padding-top:" + UnitHelper.unitToUnit(
				FunctionHelper.applyValue(parameter.margins!.top, PortalConverterHeaderFooterMargins.getTopDefault()),
				LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
			) + this.state.unit,
			"padding-bottom:" + UnitHelper.unitToUnit(
				FunctionHelper.applyValue(parameter.margins!.bottom, PortalConverterHeaderFooterMargins.getBottomDefault()),
				LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
			) + this.state.unit,
			"padding-left:" + UnitHelper.unitToUnit(
				FunctionHelper.applyValue(parameter.margins!.left, PortalConverterHeaderFooterMargins.getLeftDefault()),
				LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
			) + this.state.unit,
			"padding-right:" + UnitHelper.unitToUnit(
				FunctionHelper.applyValue(parameter.margins!.right, PortalConverterHeaderFooterMargins.getRightDefault()),
				LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
			) + this.state.unit,
			"text-align:" + parameter.alignment,
			"color:" + parameter.color,
			"width:100%"
		];

		if (!FunctionHelper.isEmpty(parameter.fontFamily)) {
			style.push("font-family:" + parameter.fontFamily!);
		}

		return '<span style="' + style.join(";") + ';">' + parameter.content + '</span>';
	}

	protected onChange(field: ConvertDialogFields, value: any): void {
		let parameter: PortalConverter = this.state.parameter!.data;
		let errors: Map<ConvertDialogFields, string> = this.state.fieldErrors;
		let pageParameter: PortalPage = parameter.page as PortalPage;
		let htmlParameter: PortalConverterHtml = parameter.html as PortalConverterHtml;
		let domParser: DOMParser = new DOMParser();

		switch (field) {
			case ConvertDialogFields.PDFA_FORMAT:
				if (FunctionHelper.isEmpty(value)) {
					parameter.archive = PortalConverter.getArchiveDefault();
					break;
				}

				parameter.archive = true;
				parameter.pdfa!.convert!.level = value;
				break;
			case ConvertDialogFields.PDFA_RESOLUTION:
				parameter.pdfa!.convert!.imageQuality = value;
				break;
			case ConvertDialogFields.LOSSLESS:
				parameter.jpegQuality = value ?
					ConvertDialogLossless.ACTIVE_VALUE :
					ConvertDialogLossless.INACTIVE_VALUE
				break;
			case ConvertDialogFields.JPEG_QUALITY:
				parameter.jpegQuality = value;
				break;
			case ConvertDialogFields.EMBED_FONTS:
				parameter.embedFonts = value;
				break;
			case ConvertDialogFields.RESOLUTION:
				parameter.reduceResolution = value;
				break;
			case ConvertDialogFields.DPI:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Converter.getDpiDefault();
				}

				parameter.dpi = value;
				break;
			case ConvertDialogFields.PAGE_FORMAT:
				pageParameter.format = value;

				let format: PaperFormat | null = PaperHelper.findFormat(value);
				if (!FunctionHelper.exists(format)) {
					break;
				}

				let width: number = pageParameter.orientation === OrientationEnum.VERTICAL ?
					format!.width : format!.height;
				let height: number = pageParameter.orientation === OrientationEnum.VERTICAL ?
					format!.height : format!.width;
				pageParameter.width = width;
				pageParameter.height = height;
				break;
			case ConvertDialogFields.HEIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getHeightDefault();
				}

				pageParameter.height = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.WIDTH:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getWidthDefault();
				}

				pageParameter.width = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.MARGIN_TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getTopDefault();
				}

				pageParameter.top = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.MARGIN_BOTTOM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getBottomDefault();
				}

				pageParameter.bottom = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.MARGIN_LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getLeftDefault();
				}

				pageParameter.left = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.MARGIN_RIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalUrlConverterPage.getRightDefault();
				}

				pageParameter.right = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.ORIENTATION:
				if (pageParameter.orientation === value) {
					break;
				}

				pageParameter.orientation = value;
				let tempSize: number = pageParameter.width!;
				pageParameter.width = pageParameter.height;
				pageParameter.height = tempSize;
				break;
			case ConvertDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.pages = value;
				break;
			case ConvertDialogFields.PASSWORD:
				parameter.accessPassword = value;
				break;
			case ConvertDialogFields.HTML_PARSED:
				parameter.fileExtension = value ? 'html' : 'txt'
				parameter.conversionAsHTML = value;
				break;
			case ConvertDialogFields.BASE_URL:
				htmlParameter.baseURL = value;
				break;
			case ConvertDialogFields.HTML_ADJUST_FONTS:
				htmlParameter.adjustFonts = value;
				break;
			case ConvertDialogFields.HTML_DOWNLOAD_IMAGES:
				htmlParameter.downloadImages = value;
				break;
			case ConvertDialogFields.HTML_USE_BACKGROUND:
				htmlParameter.useBackground = value;
				break;
			case ConvertDialogFields.SYNTAX_DETECTION:
				parameter.text!.useSyntaxDetection = value;
				break;
			case ConvertDialogFields.LANGUAGE:
				if (value === HighlighterLanguage.AUTO) {
					parameter.text!.syntaxHighlight!.available = undefined;
					parameter.text!.syntaxHighlight!.language = undefined;
				}

				parameter.text!.syntaxHighlight!.available = value;
				parameter.text!.syntaxHighlight!.language = value;
				break;
			case ConvertDialogFields.FONT_FAMILY:
				if (parameter.text?.useSyntaxDetection) {
					parameter.text!.syntaxHighlight!.fontFamily = value;
				} else {
					parameter.text!.textHighlight!.fontFamily = value;
				}
				break;
			case ConvertDialogFields.WORD_BREAK:
				if (parameter.text?.useSyntaxDetection) {
					parameter.text!.syntaxHighlight!.wordBreak = value;
				} else {
					parameter.text!.textHighlight!.wordBreak = value;
				}
				break;
			case ConvertDialogFields.LINE_HEIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = TextHighlight.getLineHeightDefault();
				}

				if (parameter.text?.useSyntaxDetection) {
					parameter.text!.syntaxHighlight!.lineHeight = value + LengthUnit.PIXEL;
				} else {
					parameter.text!.textHighlight!.lineHeight = value + LengthUnit.PIXEL;
				}
				break;
			case ConvertDialogFields.FONT_SIZE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = TextHighlight.getFontSizeDefault();
				}

				if (parameter.text?.useSyntaxDetection) {
					parameter.text!.syntaxHighlight!.fontSize = value + LengthUnit.PIXEL;
				} else {
					parameter.text!.textHighlight!.fontSize = value + LengthUnit.PIXEL;
				}
				break;
			case ConvertDialogFields.LINE_NUMBERS:
				if (parameter.text?.useSyntaxDetection) {
					parameter.text!.syntaxHighlight!.lineNumbers = value;
				} else {
					parameter.text!.textHighlight!.lineNumbers = value;
				}
				break;
			case ConvertDialogFields.FONT_COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.text!.textHighlight!.color = value;
				break;
			case ConvertDialogFields.XML_MODE:
				parameter.xmlMode = value;
				parameter.xRechnung = value === XmlMode.XRECHNUNG;
				parameter.xslTransformation = value === XmlMode.TRANSFORM;
				parameter.text!.syntaxHighlight!.available = "xml";
				parameter.text!.syntaxHighlight!.language = "xml";
				parameter.text!.useSyntaxDetection = true;
				break;
			case ConvertDialogFields.XSLT_FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let currentXsltFile: File = value[0];
				let xsltFiles: Array<File> = value;
				for (let file of xsltFiles) {
					currentXsltFile = file;

					let fileDataType: PortalXslTransformationFile =
						PortalXslTransformationFile.fromJson({
							id: Math.random().toString(36).substring(2, 15) +
								Math.random().toString(36).substring(2, 15) + file.name,
							fileName: file.name,
							mimetype: file.mimetype,
							value: file.value,
							format: XslTransformationFileFormat.Other,
						} as PortalXslTransformationFile);

					if (file.value !== '' && file.mimetype === MimeTypes.XML.toString()) {
						let xmlDoc: Document = domParser.parseFromString(Base64.atob(file.value), "text/xml");

						if (
							xmlDoc && xmlDoc.getElementsByTagNameNS(NAMESPACE_STYLESHEET, XML_TAG_STYLESHEET).length > 0
						) {
							fileDataType.format = XslTransformationFileFormat.Xslt;
						}
					}

					parameter.transform!.xslTransformation!.resources!.file!.push(fileDataType);
				}

				this.setState({
					xsltFile: currentXsltFile,
					parameter: this.state.parameter
				});
				return;
			case ConvertDialogFields.XRECHNUNG_OVERWRITE:
				parameter.xRechnungOverwrite = value;
				break;
			case ConvertDialogFields.XRECHNUNG_FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let currentXRechnungFile: File = value[0];
				let xrechnungFiles: Array<File> = value;
				for (let file of xrechnungFiles) {
					currentXRechnungFile = file;

					let fileDataType: PortalXslTransformationFile = PortalXslTransformationFile.fromJson({
						id: Math.random().toString(36).substring(2, 15) +
							Math.random().toString(36).substring(2, 15) + file.name,
						fileName: file.name,
						mimetype: file.mimetype,
						value: file.value,
						format: XslTransformationFileFormat.Other,
					});

					if (file.value !== '' && file.mimetype === MimeTypes.XML.toString()) {
						let xmlDoc: Document = domParser.parseFromString(Base64.atob(file.value), "text/xml");

						if (
							xmlDoc && xmlDoc.getElementsByTagNameNS(NAMESPACE_STYLESHEET, XML_TAG_STYLESHEET).length > 0
						) {
							fileDataType.format = XslTransformationFileFormat.Xslt;
						}
					}

					parameter.transform!.xRechnung!.visualization!.file!.push(fileDataType);
				}

				this.setState({
					xrechnungFile: currentXRechnungFile,
					parameter: this.state.parameter
				});
				return;
			case ConvertDialogFields.EMAIL_ATTACHMENT:
				parameter.mail!.attachmentMode = value;
				break;
			case ConvertDialogFields.EMAIL_ADJUST_FONTS:
				parameter.mail!.adjustFonts = value;
				break;
			case ConvertDialogFields.EMAIL_DOWNLOAD_IMAGES:
				parameter.mail!.downloadImages = value;
				break;
			case ConvertDialogFields.EMAIL_RESIZE_IMAGES:
				parameter.mail!.resizeImages = value;
				break;
			case ConvertDialogFields.EMAIL_RESIZE_TABLES:
				parameter.mail!.resizeTables = value;
				break;
			case ConvertDialogFields.EMAIL_USE_BACKGROUND:
				parameter.mail!.useBackground = value;
				break;
			case ConvertDialogFields.TEMPLATE_LANGUAGE:
				parameter.template!.language = value;
				break;
			case ConvertDialogFields.UNIT:
				this.setState({
					unit: value
				});
				pageParameter.unit = value;
				break;
			case ConvertDialogFields.ASPECT_RATIO:
				parameter.image!.maintainAspectRatio = value;
				break;
			case ConvertDialogFields.HEADER_PLAIN:
				parameter.header!.content = this.convertContents(parameter.header!);
				parameter.header!.plain = !value;
				break;
			case ConvertDialogFields.HEADER_FONT_FAMILY:
				parameter.header!.fontFamily = value;
				break;
			case ConvertDialogFields.HEADER_FONT_SIZE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooter.getFontSizeDefault();
				}

				parameter.header!.fontSize = value;
				break;
			case ConvertDialogFields.HEADER_FONT_COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.header!.color = value;
				break;
			case ConvertDialogFields.HEADER_ALIGNMENT:
				parameter.header!.alignment = value;
				break;
			case ConvertDialogFields.HEADER_CONTENT:
				parameter.header!.content = value;
				break;
			case ConvertDialogFields.HEADER_MARGIN_TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getTopDefault();
				}

				parameter.header!.margins!.top = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.HEADER_MARGIN_BOTTOM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getBottomDefault();
				}

				parameter.header!.margins!.bottom = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.HEADER_MARGIN_LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getLeftDefault();
				}

				parameter.header!.margins!.left = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.HEADER_MARGIN_RIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getRightDefault();
				}

				parameter.header!.margins!.right = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.FOOTER_PLAIN:
				parameter.footer!.content = this.convertContents(parameter.footer!);
				parameter.footer!.plain = !value;
				break;
			case ConvertDialogFields.FOOTER_FONT_FAMILY:
				parameter.footer!.fontFamily = value;
				break;
			case ConvertDialogFields.FOOTER_FONT_SIZE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooter.getFontSizeDefault();
				}

				parameter.footer!.fontSize = value;
				break;
			case ConvertDialogFields.FOOTER_FONT_COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.footer!.color = value;
				break;
			case ConvertDialogFields.FOOTER_ALIGNMENT:
				parameter.footer!.alignment = value;
				break;
			case ConvertDialogFields.FOOTER_CONTENT:
				parameter.footer!.content = value;
				break;
			case ConvertDialogFields.FOOTER_MARGIN_TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getTopDefault();
				}

				parameter.footer!.margins!.top = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.FOOTER_MARGIN_BOTTOM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getBottomDefault();
				}

				parameter.footer!.margins!.bottom = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.FOOTER_MARGIN_LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getLeftDefault();
				}

				parameter.footer!.margins!.left = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.FOOTER_MARGIN_RIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalConverterHeaderFooterMargins.getRightDefault();
				}

				parameter.footer!.margins!.right = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case ConvertDialogFields.CONVERTER:
				parameter.converterType = value;
				break;
			case ConvertDialogFields.HTML_USE_AS_TEMPLATE:
				htmlParameter.useAsTemplate = value;
				break;
			case ConvertDialogFields.HTML_TEMPLATE_USE_FILE:
				htmlParameter.useTemplateFile = !value;
				break;
			case ConvertDialogFields.HTML_TEMPLATE_FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let file: File = value;
				let fileDataType: TemplateData = TemplateData.fromJson({
					name: file.name,
					mimetype: file.mimetype,
					value: file.value
				} as TemplateDataInterface);
				htmlParameter.templateData = fileDataType;

				this.setState({
					templateFile: file,
					parameter: this.state.parameter
				});
				return;
			case ConvertDialogFields.HTML_TEMPLATE_CONTENT:
				htmlParameter.templateContent = value;
				break;
			case ConvertDialogFields.HTML_ERROR_REPORT:
				htmlParameter.errorReport!.mode = (value) ?
					ConverterHtmlErrorReportMode.Message : ConverterHtmlErrorReportMode.None;
				break;
			case ConvertDialogFields.HTML_ERROR_REPORT_FORMAT:
				htmlParameter.errorReport!.format = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected async submit(): Promise<void> {
		let parameter: PortalConverter = this.state.parameter!.data;

		if (parameter.xRechnungOverwrite && parameter.xRechnung && FunctionHelper.exists(parameter.transform)) {
			let hasXSLTFile: boolean = false;

			for (let file of FunctionHelper.applyValue(
				parameter.transform?.xRechnung?.visualization?.file, XRechnungResources.getFileDefault()
			)) {
				if (file.format === XslTransformationFileFormat.Xslt) {
					hasXSLTFile = true;
					break;
				}
			}

			if (!hasXSLTFile) {
				await this.onError(new Error(
					Translate.value("errors.converter.xslt", {ns: "message"})
				));
				return;
			}
		}

		if (parameter.xslTransformation && FunctionHelper.exists(parameter.transform) &&
			FunctionHelper.applyValue(parameter.transform?.xslTransformation?.resources?.file?.length, 0) > 0) {
			let hasXSLTFile: boolean = false;

			for (let file of FunctionHelper.applyValue(
				parameter.transform?.xslTransformation?.resources?.file, XslTransformationResources.getFileDefault()
			)) {
				if (file.format === XslTransformationFileFormat.Xslt) {
					hasXSLTFile = true;
					break;
				}
			}

			if (!hasXSLTFile) {
				await this.onError(new Error(
					Translate.value("errors.converter.xslt", {ns: "message"})
				));
				return;
			}
		}

		await super.submit();

		let portalInfo = this.props.context.info;
		let operationData: PortalConverter = this.state.parameter!.data.clone();
		let pageParameter: PortalPage = operationData.page as PortalPage;
		let htmlParameter: PortalConverterHtml = operationData.html as PortalConverterHtml;

		if (!FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.converter?.executables?.sharepoint?.enabled,
			ExecutableStatus.getStatusDefault()
		)) {
			operationData.sharePointBridge = undefined;
		}

		if (!FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.converter?.executables?.office?.enabled,
			ExecutableStatus.getStatusDefault()
		)) {
			operationData.officeBridge = undefined;
		}

		if (!operationData.archive) {
			operationData.pdfa = undefined;
		}

		if (FunctionHelper.exists(htmlParameter)) {
			if (!parameter.conversionAsHTML) {
				htmlParameter.useAsTemplate = false;
			}

			if (htmlParameter.useAsTemplate && !htmlParameter.useTemplateFile) {
				htmlParameter.templateData!.value = Base64.encode(FunctionHelper.applyValue(
					htmlParameter.templateContent, PortalConverterHtml.getTemplateContentDefault()
				));
			}
		}

		if (!parameter.xRechnung && FunctionHelper.exists(operationData.transform)) {
			operationData.transform!.xRechnung = undefined;
		} else if (!parameter.xRechnungOverwrite && FunctionHelper.exists(operationData.transform)) {
			operationData.transform!.xRechnung!.visualization = undefined;
		}

		if (!operationData.xslTransformation && FunctionHelper.exists(operationData.transform)) {
			operationData.transform!.xslTransformation = undefined;
		} else if (FunctionHelper.exists(operationData.transform?.xslTransformation) &&
			FunctionHelper.applyValue(parameter.transform?.xslTransformation?.resources?.file?.length, 0) === 0) {
			operationData.transform!.xslTransformation!.resources = undefined;
		}

		if (!operationData.xRechnung && !operationData.xslTransformation) {
			operationData.transform = undefined;
		}

		if (operationData.xmlMode !== XmlMode.TEXT) {
			operationData.text = undefined;
		}

		if (FunctionHelper.exists(operationData.header) &&
			FunctionHelper.applyValue(operationData.header?.plain, PortalConverterHeaderFooter.getPlainDefault())) {
			operationData.header!.content = this.convertContents(operationData.header!);
		}

		if (FunctionHelper.exists(operationData.footer) &&
			FunctionHelper.applyValue(operationData.footer?.plain, PortalConverterHeaderFooter.getPlainDefault())) {
			operationData.footer!.content = this.convertContents(operationData.footer!);
		}

		if (FunctionHelper.exists(pageParameter)) {
			if (pageParameter.format === FormatEnum.ORIGINAL) {
				operationData.page = undefined;
			} else {
				operationData.page = pageParameter;
			}
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		let operation: ConcurrentOperation = new ConcurrentOperation(this.props.concurrentOperations);
		for (let portalDocument of selection) {
			if (FileTypeGroupFilter.checkSelection(
				[portalDocument.getFileTypeGroup()], [FileTypeGroup.MAIL, FileTypeGroup.TEXT]
			) || MimeTypeFilter.checkSelection(
				[portalDocument.getMimeType()], [
					MimeTypes.XML, MimeTypes.APPXML, MimeTypes.APPXSLTXML, MimeTypes.HTML
				]
			)) {
				if (FunctionHelper.exists(operationData.header) &&
					!FunctionHelper.isEmpty(operationData.header?.content)) {
					operationData.page!.header!.value = Base64.encode(FunctionHelper.applyValue(
						operationData.header?.content, PortalConverterHeaderFooter.getContentDefault()
					));
				} else {
					operationData.page!.header = undefined;
				}

				if (FunctionHelper.exists(operationData.footer) &&
					!FunctionHelper.isEmpty(operationData.footer?.content)) {
					operationData.page!.footer!.value = Base64.encode(FunctionHelper.applyValue(
						operationData.footer?.content, PortalConverterHeaderFooter.getContentDefault()
					));
				} else {
					operationData.page!.footer = undefined;
				}
			}

			let internal: Internal | undefined;
			switch (operationData.converterType) {
				case ConverterType.OFFICE_BRIDGE:
					operationData.sharePointBridge = undefined;
					operationData.officeBridge = OfficeBridge.fromJson({});
					break;
				case ConverterType.SHAREPOINT:
					operationData.officeBridge = undefined;
					operationData.sharePointBridge = SharePointBridge.fromJson({});
					break;
				case ConverterType.SHAREPOINT_ONLINE:
					operationData.officeBridge = undefined;
					operationData.sharePointBridge = undefined;
					internal = Internal.fromJson({
						sharePointOnlineBridge: {}
					} as InternalInterface);
					break;
				default:
					operationData.sharePointBridge = undefined;
					operationData.officeBridge = undefined;
					break;
			}

			operation.add(async (resolve: () => void, reject: (exception: Error) => void) => {
				try {
					await this.props.context.session.executeRequest({
						webServiceType: WebServiceTypes.CONVERTER,
						document: portalDocument,
						operation: Converter.fromJson(operationData.toJson()),
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as SettingsInterface),
						internal: internal
					});

					resolve();
				} catch (exception: any) {
					if (this.props.concurrentOperations > 1) {
						let portalException: Exception = Exception.parseException(exception);
						portalDocument.setExecutionError(portalException);
					}

					reject(exception);
				}
			});
		}

		try {
			await operation.execute();
			let message: Message = new Message("success.converter.convert", MessageType.SUCCESS);
			message.context = {count: operation.length};
			this.props.context.getPage().addMessage(message);
		} catch (exception: any) {
			let errorReport: undefined | ConverterHtmlErrorReportFormat;
			if (htmlParameter?.errorReport?.mode === ConverterHtmlErrorReportMode.Message) {
				errorReport = htmlParameter?.errorReport?.format;
			}

			this.setState({
				errorReport: errorReport
			});

			if (await this.onError(exception)) {
				await this.submit();
				return;
			}

			if (this.props.concurrentOperations > 1) {
				documentManager.updateGlobalDocumentList();
				this.hide();
			}
			return;
		}

		this.hide();
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalConverter | undefined = this.state.parameter?.data;
		let portalDocuments: Array<PortalDocument> = this.props.context.documentManager.getSelection();

		if (!FunctionHelper.exists(parameter?.page)) {
			return;
		}

		let pageParameter: PortalPage = parameter!.page as PortalPage;

		let fileTypeGroups: Array<string> = [];
		for (let selectedDocument of portalDocuments) {
			fileTypeGroups.push(selectedDocument.getFileTypeGroup());
		}

		let currentPage: string = "1";
		let pageCount: number = 1;
		if (portalDocuments.length === 1) {
			let portalDocument: PortalDocument = portalDocuments[0];
			let documentSettings: DocumentSettings = portalDocument.getSettings();

			if (!FunctionHelper.exists(portalDocument)) {
				return;
			}

			currentPage = documentSettings.activeViewer.currentPage.toString();
			pageCount = await portalDocument.getNumberOfPages();

			if (MimeTypeFilter.check(portalDocument.getMimeType(), [
				MimeTypes.HEIF, MimeTypes.HEIC, MimeTypes.HEIF_SEQUENCE, MimeTypes.HEIC_SEQUENCE, MimeTypes.TIFF
			])) {
				parameter!.pages = documentSettings.pageSelection.toString(
					pageCount, documentSettings.activeViewer.currentPage
				);
			}

			if (MimeTypeFilter.check(portalDocument.getMimeType(), [MimeTypes.HTML])) {
				parameter!.conversionAsHTML = documentSettings.viewAsHTML;
			}

			if (FileTypeGroupFilter.checkSelection(fileTypeGroups, [FileTypeGroup.TEXT]) &&
				FunctionHelper.applyValue(
					this.props.context.info.webserviceStatus?.webservices?.converter?.executables?.chromium?.enabled,
					ExecutableStatus.getEnabledDefault()
				)) {

				let useHighlighter: boolean = false;
				if (documentSettings.hightlighterLanguage !== HighlighterLanguage.AUTO) {
					useHighlighter = true;
					parameter!.text!.syntaxHighlight!.available = documentSettings.hightlighterLanguage;
					parameter!.text!.syntaxHighlight!.language = documentSettings.hightlighterLanguage;
				} else if (portalDocument.data.relevance >= 50) {
					useHighlighter = true;
					parameter!.text!.syntaxHighlight!.available = portalDocument.data.language!;
					parameter!.text!.syntaxHighlight!.language = portalDocument.data.language!;
				}

				parameter!.text!.useSyntaxDetection = useHighlighter;
			}
		}

		if (FileTypeGroupFilter.checkSelection(
			fileTypeGroups, [], [FileTypeGroup.PDF]
		)) {
			pageCount = Number.MAX_VALUE;
		}

		let hasPages: boolean = true;
		for (let portalDocument of portalDocuments) {
			if (await portalDocument.getNumberOfPages() <= 1) {
				hasPages = false
			}
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			panel: 0,
			pages: pageCount,
			currentPage: currentPage,
			hasPages: hasPages,
			unit: FunctionHelper.applyValue(pageParameter.unit, PortalPage.getUnitDefault())
		});
	};

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-convert small"}/>
					<Translate {...this.props} data="convert.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.CONVERT;
	}

	protected getDefaultParameter(): Parameter<PortalConverter> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		this.setState({
			panel: 0
		});
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (event.key.toLowerCase() === "enter" && (this.state.panel === 2 || this.state.panel === 3)) {
			return;
		}

		await super.handleKeydown(event);
	}
}

const ConvertDialog = React.memo(ConvertDialogDefinition,
	(prevProps: Readonly<ConvertDialogProperties>, nextProps: Readonly<ConvertDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default ConvertDialog;